// Comp off as a row of the leave balances (owner, 2026-10-09: "merge comp off
// leaves into leave balances"). Laid out exactly like a leave type's row in
// HR's Leave balances card: name on the left, the yearly limit on the right,
// a bar, then what is credited and what is left. Over the limit is said in
// amber, never hidden — a legacy per-day credit can put someone past it (bug
// report 2026-10-08). Since 2026-10-22 comp off is this count alone and never
// changes a leave balance ("leaves used are leaves used"): a month credits only
// while its leave type (Casual) has room under its yearly standard, the days
// used less comp off already credited this year, and drops the rest
// (20261022100000).
import { Badge, Mono, Progress } from './primitives';
import { useCompOff } from '../lib/compOff';

const days = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;

/** Renders nothing while overtime is off. */
export function CompOffRow({ profileId, year }: { profileId: string; year: number }) {
  const c = useCompOff(profileId, year);
  if (!c) return null;
  const over = c.cap != null && c.credited > c.cap;
  const into = c.into ?? 'leave';
  return (
    <div
      role="group"
      aria-label="Comp off"
      style={{
        padding: 'var(--hf-space-3) 0',
        borderBottom: '1px solid var(--hf-rule)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--hf-space-3)',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--hf-space-2) var(--hf-space-3)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--hf-space-2)' }}>
          <span style={{ fontSize: 'var(--hf-text-md)', color: 'var(--hf-ink)' }}>Comp off</span>
          <Badge tone="neutral">counter</Badge>
        </div>
        <Mono>{c.cap != null ? `${c.cap} a year` : 'no yearly limit'}</Mono>
      </div>
      <div>
        {c.cap != null && c.cap > 0 && (
          <Progress
            value={(c.credited / c.cap) * 100}
            tone={over ? 'amber' : 'accent'}
            size={4}
            label={`${c.credited} of ${c.cap} comp off days credited this year`}
          />
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 'var(--hf-space-1)',
          }}
        >
          <Mono dim>{c.credited} credited</Mono>
          <Mono style={{ color: over ? 'var(--hf-amber-text)' : 'var(--hf-ink-2)' }}>
            {over
              ? `${days(Math.round((c.credited - (c.cap ?? 0)) * 10) / 10)} over the limit`
              : c.left != null
                ? `${c.left} left`
                : ''}
          </Mono>
        </div>
        <div
          style={{
            marginTop: 'var(--hf-space-1)',
            fontSize: 'var(--hf-text-xs)',
            color: 'var(--hf-ink-3)',
          }}
        >
          Credited from overtime when each month’s payroll is locked
          {c.halfDayHours ? `, ½ day for every ${c.halfDayHours}h` : ''}. Counted against the yearly
          limit. Comp off is counted here and never changes {into}: days you used stay used. A month
          credits only while {into} has room under its yearly standard (the standard less the {into}{' '}
          days you have left, less comp off already credited this year); overtime past that is
          dropped.
        </div>
      </div>
    </div>
  );
}
